import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormErrorSummary, TextareaField, VersionHistory, zRequiredString } from '@/business';
import { AlertDialog, AlertDialogContent, Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError } from '@/shared/api';

import { useRemoveOtherDocument, useWaiveDocument } from '../api/mutations';
import { dealDocumentVersionsQuery, fetchDealDocumentVersionLink } from '../api/queries';
import { waiveSchema, type WaiveValues } from '../forms/documentUpload';

type Doc = Schema<'DealDocument'>;

const rejectSchema = z.object({ reason: zRequiredString() });

/** Reject the current evidence (reason required, shown to the uploader). */
export function RejectDialog({ doc, onClose, onReject }: { doc: Doc | null; onClose: () => void; onReject: (reason: string) => Promise<void> }) {
  const { t } = useTranslation('deals');
  const form = useForm<{ reason: string }>({ resolver: zodResolver(rejectSchema), defaultValues: { reason: '' } });
  const submit = form.handleSubmit(async (v) => {
    await onReject(v.reason.trim());
    form.reset();
  });
  return (
    <Dialog open={Boolean(doc)} onOpenChange={(o) => !o && onClose()}>
      {doc ? (
        <DialogContent
          title={t('documents.rejectTitle', { name: doc.name })}
          description={t('documents.rejectBody')}
          footer={
            <>
              <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
              <Button variant="danger-solid" loading={form.formState.isSubmitting} onClick={() => void submit()}>
                {t('documents.reject')}
              </Button>
            </>
          }
        >
          <form onSubmit={(e) => void submit(e)}>
            <TextareaField control={form.control} name="reason" label={t('documents.reason')} placeholder={t('documents.reasonPlaceholder')} rows={3} />
          </form>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

/** Waive a requirement that is no longer needed (document.review; reason required; audited). */
export function WaiveDialog({ dealId, doc, onClose }: { dealId: string; doc: Doc; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const waive = useWaiveDocument(dealId);
  const [problem, setProblem] = useState<string | null>(null);
  const form = useForm<WaiveValues>({ resolver: zodResolver(waiveSchema), defaultValues: { reason: '' } });
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await waive.mutateAsync({ documentId: doc.id, reason: v.reason.trim() });
      toast.done(t('documents.waive.done', { name: doc.name }));
      onClose();
    } catch (e) {
      setProblem(apiErrorMessage(e));
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={t('documents.waive.title', { name: doc.name })}
        description={t('documents.waive.body')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={waive.isPending} onClick={() => void submit()}>
              {t('documents.waive.confirm')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3">
          {problem ? <FormErrorSummary title={problem} /> : null}
          <TextareaField control={form.control} name="reason" label={t('documents.waive.reason')} placeholder={t('documents.waive.reasonPlaceholder')} rows={3} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Version history of one requirement's document (loaded when opened). */
export function DocumentVersions({ dealId, documentId }: { dealId: string; documentId: string }) {
  const versions = useQuery(dealDocumentVersionsQuery(dealId, documentId));
  return (
    <VersionHistory
      versions={versions.data}
      loading={versions.isPending}
      error={versions.isError ? apiErrorMessage(versions.error) : null}
      fetchVersionLink={(versionId, disposition) => fetchDealDocumentVersionLink(dealId, documentId, versionId, disposition)}
    />
  );
}

/** Remove an "Other document" the deal no longer needs (its file versions stay for the audit trail). */
export function RemoveDocumentDialog({ dealId, doc, onClose }: { dealId: string; doc: Doc; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const remove = useRemoveOtherDocument(dealId);
  const run = async () => {
    try {
      await remove.mutateAsync(doc.id);
      toast.done(t('documents.other.removed', { name: doc.name }));
      onClose();
    } catch (e) {
      toast.error(removeErrorMessage(t, e));
    }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent tone="danger" title={t('documents.other.removeTitle', { name: doc.name })} description={t('documents.other.removeBody')} confirmLabel={t('documents.other.remove')} loading={remove.isPending} onConfirm={() => void run()} />
    </AlertDialog>
  );
}

/** A refused removal says why (approved copy, not an other document); otherwise the server's message. */
function removeErrorMessage(t: (k: 'documents.other.refused.document_approved' | 'documents.other.refused.not_custom') => string, e: unknown): string {
  const reason = isApiError(e) ? e.details['reason'] : null;
  if (reason === 'document_approved' || reason === 'not_custom') return t(`documents.other.refused.${reason}`);
  return apiErrorMessage(e);
}
